import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rolesService } from "../../services";
import AdminModalTailwind from "./AdminModalTailwind";

const PERMISSION_GROUPS_CONFIG = {
  general: { titleKey: "admin.permissions.group.general", defaultTitle: "Allgemein" },
  participants: { titleKey: "admin.permissions.group.participants", defaultTitle: "Teilnehmer & Nationen" },
  organisation: { titleKey: "admin.permissions.group.organisation", defaultTitle: "Organisation" },
  results: { titleKey: "admin.permissions.group.results", defaultTitle: "Ergebnisse" },
  security: { titleKey: "admin.permissions.group.security", defaultTitle: "Sicherheit" }
};

const PERMISSION_CONFIG = {
  view_dashboard: {
    group: "general",
    labelKey: "admin.permissions.view_dashboard.label",
    defaultLabel: "Dashboard anzeigen",
    descriptionKey: "admin.permissions.view_dashboard.description",
    defaultDescription: "Gewährt Zugriff auf die Dashboard-Übersicht."
  },
  view_results: {
    group: "results",
    labelKey: "admin.permissions.view_results.label",
    defaultLabel: "Ergebnisse einsehen",
    descriptionKey: "admin.permissions.view_results.description",
    defaultDescription: "Erlaubt die Einsicht in veröffentlichte Ergebnisse."
  },
  manage_participants: {
    group: "participants",
    labelKey: "admin.permissions.manage_participants.label",
    defaultLabel: "Teilnehmer verwalten",
    descriptionKey: "admin.permissions.manage_participants.description",
    defaultDescription: "Teilnehmer anlegen, bearbeiten und löschen."
  },
  manage_nations: {
    group: "participants",
    labelKey: "admin.permissions.manage_nations.label",
    defaultLabel: "Nationen verwalten",
    descriptionKey: "admin.permissions.manage_nations.description",
    defaultDescription: "Zugriff auf die Verwaltung der Nationenliste."
  },
  manage_categories: {
    group: "participants",
    labelKey: "admin.permissions.manage_categories.label",
    defaultLabel: "Kategorien verwalten",
    descriptionKey: "admin.permissions.manage_categories.description",
    defaultDescription: "Anlegen und Bearbeiten von Wettkampfkategorien."
  },
  manage_groups: {
    group: "organisation",
    labelKey: "admin.permissions.manage_groups.label",
    defaultLabel: "Gruppen verwalten",
    descriptionKey: "admin.permissions.manage_groups.description",
    defaultDescription: "Zugriff auf Gruppeneinteilungen und Startlisten."
  },
  manage_schedule: {
    group: "organisation",
    labelKey: "admin.permissions.manage_schedule.label",
    defaultLabel: "Zeitplan verwalten",
    descriptionKey: "admin.permissions.manage_schedule.description",
    defaultDescription: "Planung und Anpassung der Wettkampfzeitpläne."
  },
  manage_schedule_settings: {
    group: "organisation",
    labelKey: "admin.permissions.manage_schedule_settings.label",
    defaultLabel: "Zeitplan-Konfiguration verwalten",
    descriptionKey: "admin.permissions.manage_schedule_settings.description",
    defaultDescription: "Zugriff auf Zeitplan-Konfiguration und Einstellungen."
  },
  view_caller: {
    group: "organisation",
    labelKey: "admin.permissions.view_caller.label",
    defaultLabel: "Zugriff auf Calleransicht",
    descriptionKey: "admin.permissions.view_caller.description",
    defaultDescription: "Gewährt Zugriff auf die Caller-Ansicht für Athleten-Aufruf."
  },
  manage_results: {
    group: "results",
    labelKey: "admin.permissions.manage_results.label",
    defaultLabel: "Ergebnisse verwalten",
    descriptionKey: "admin.permissions.manage_results.description",
    defaultDescription: "Bewertungen erfassen, veröffentlichen und anpassen."
  },
  manage_users: {
    group: "security",
    labelKey: "admin.permissions.manage_users.label",
    defaultLabel: "Benutzer verwalten",
    descriptionKey: "admin.permissions.manage_users.description",
    defaultDescription: "Benutzerkonten anlegen, bearbeiten und sperren."
  },
  manage_roles: {
    group: "security",
    labelKey: "admin.permissions.manage_roles.label",
    defaultLabel: "Rollen & Berechtigungen",
    descriptionKey: "admin.permissions.manage_roles.description",
    defaultDescription: "Rollen definieren und deren Berechtigungen anpassen."
  },
  manage_data: {
    group: "security",
    labelKey: "admin.permissions.manage_data.label",
    defaultLabel: "Datenmanagement",
    descriptionKey: "admin.permissions.manage_data.description",
    defaultDescription: "Zugriff auf Datenmanagement und Systemeinstellungen."
  }
};

const PERMISSION_GROUP_ORDER = ["general", "participants", "organisation", "results", "security"];

function UserRolesTailwind() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [isRoleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", description: "", permissions: [] });
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await rolesService.getRoles();
      if (!res.success) throw new Error(res.error || "Fehler beim Laden der Rollen");
      return res.data || [];
    }
  });

  const permissionsQuery = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const res = await rolesService.getPermissions();
      if (!res.success) throw new Error(res.error || "Fehler beim Laden der Berechtigungen");
      return res.data || [];
    }
  });

  const addToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: "info", message: "" }), 5000);
  };

  const hideToast = () => setToast({ show: false, type: "info", message: "" });

  const createRoleMutation = useMutation({
    mutationFn: rolesService.createRole.bind(rolesService),
    onSuccess: () => {
      queryClient.invalidateQueries(["roles"]);
      addToast("success", t("admin.userRoles.toast.created", "Rolle erfolgreich erstellt"));
      setRoleModalOpen(false);
      setEditingRole(null);
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.userRoles.toast.createError", "Fehler beim Anlegen der Rolle"));
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, payload }) => rolesService.updateRole(id, payload),
    onMutate: (variables) => setUpdatingRoleId(variables.id),
    onSuccess: () => {
      queryClient.invalidateQueries(["roles"]);
      addToast("success", t("admin.userRoles.toast.updated", "Rolle aktualisiert"));
      setRoleModalOpen(false);
      setEditingRole(null);
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.userRoles.toast.updateError", "Fehler beim Aktualisieren der Rolle"));
    },
    onSettled: () => setUpdatingRoleId(null)
  });

  const deleteRoleMutation = useMutation({
    mutationFn: rolesService.deleteRole.bind(rolesService),
    onSuccess: () => {
      queryClient.invalidateQueries(["roles"]);
      addToast("success", t("admin.userRoles.toast.deleted", "Rolle gelöscht"));
      setDeleteModalOpen(false);
      setRoleToDelete(null);
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.userRoles.toast.deleteError", "Fehler beim Löschen der Rolle"));
    }
  });

  const availablePermissions = permissionsQuery.data || [];
  const availablePermissionKeys = new Set(availablePermissions.map((p) => p.key));

  const permissionGroups = useMemo(() => {
    const groupsMap = new Map();

    PERMISSION_GROUP_ORDER.forEach((groupId) => {
      const config = PERMISSION_GROUPS_CONFIG[groupId];
      groupsMap.set(groupId, {
        id: groupId,
        title: t(config?.titleKey || "", config?.defaultTitle || groupId),
        permissions: []
      });
    });

    availablePermissions.forEach((permission) => {
      // Nur Permissions anzeigen, die im PERMISSION_CONFIG definiert sind
      const config = PERMISSION_CONFIG[permission.key];
      if (!config) {
        return; // Überspringe ungenutzte Permissions (manage_settings, view_schedule, view_participants)
      }
      
      const groupId = config.group || "general";
      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, {
          id: groupId,
          title: t(PERMISSION_GROUPS_CONFIG[groupId]?.titleKey || "", PERMISSION_GROUPS_CONFIG[groupId]?.defaultTitle || groupId),
          permissions: []
        });
      }
      const group = groupsMap.get(groupId);
      group.permissions.push({
        key: permission.key,
        label: t(config.labelKey || "", config.defaultLabel || permission.key),
        description: t(config.descriptionKey || "", config.defaultDescription || permission.description || "")
      });
    });

    return Array.from(groupsMap.values()).filter((group) => group.permissions.length > 0);
  }, [availablePermissions, t]);

  const roles = rolesQuery.data || [];

  const openCreateModal = () => {
    setEditingRole(null);
    setRoleForm({ name: "", description: "", permissions: [] });
    setRoleModalOpen(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name || "",
      description: role.description || "",
      permissions: role.permissions ? role.permissions.map((p) => p.key) : []
    });
    setRoleModalOpen(true);
  };

  const closeRoleModal = () => {
    if (createRoleMutation.isLoading || updateRoleMutation.isLoading) return;
    setRoleModalOpen(false);
    setEditingRole(null);
  };

  const handleRoleFormChange = (event) => {
    const { name, value } = event.target;
    setRoleForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleFormPermissionToggle = (permissionKey) => {
    setRoleForm((prev) => {
      const hasPermission = prev.permissions.includes(permissionKey);
      return {
        ...prev,
        permissions: hasPermission
          ? prev.permissions.filter((key) => key !== permissionKey)
          : [...prev.permissions, permissionKey]
      };
    });
  };

  const handleSubmitRoleForm = () => {
    const payload = {
      name: roleForm.name.trim(),
      description: roleForm.description.trim(),
      permissions: roleForm.permissions.filter((key) => availablePermissionKeys.has(key))
    };

    if (!payload.name) {
      addToast("warning", t("admin.userRoles.validation.nameRequired", "Der Rollenname darf nicht leer sein."));
      return;
    }

    if (editingRole) {
      updateRoleMutation.mutate({ id: editingRole.id, payload });
    } else {
      createRoleMutation.mutate(payload);
    }
  };

  const handleTogglePermission = (role, permissionKey, enable) => {
    const currentKeys = role.permissions ? role.permissions.map((p) => p.key) : [];
    const nextKeys = enable
      ? Array.from(new Set([...currentKeys, permissionKey]))
      : currentKeys.filter((key) => key !== permissionKey);

    updateRoleMutation.mutate({ id: role.id, payload: { permissions: nextKeys } });
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteRoleMutation.isLoading) return;
    setDeleteModalOpen(false);
    setRoleToDelete(null);
  };

  if (rolesQuery.isLoading || permissionsQuery.isLoading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              <div className="flex gap-2">
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
            </div>
          </div>

          {/* Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-4"></div>
                <div className="space-y-3">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (rolesQuery.isError || permissionsQuery.isError) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">
                {rolesQuery.error?.message || permissionsQuery.error?.message || t("admin.userRoles.error.load", "Fehler beim Laden der Rollen.")}
              </p>
              <button
                onClick={() => {
                  rolesQuery.refetch();
                  permissionsQuery.refetch();
                }}
                className="bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-6 py-3 rounded-md transition-colors duration-200 font-medium"
              >
                {t("admin.retry", "Erneut versuchen")}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t("admin.userRoles.title", "Nutzerrollen & Berechtigungen")}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                {t("admin.userRoles.subtitleReal", "Verwalte Rollen, definiere Berechtigungen und steuere den Zugriff auf Verwaltungsbereiche.")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-4 h-[46px] bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <i className="fas fa-shield-alt text-xs"></i>
                {t("admin.userRoles.actions.addRole", "Rolle hinzufügen")}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {roles.map((role) => {
            const isUpdating = updatingRoleId === role.id;
            const rolePermissions = new Set(role.permissions ? role.permissions.map((p) => p.key) : []);
            return (
              <div key={role.id} className="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm p-6">
                {isUpdating && (
                  <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/60 backdrop-blur-[1px] rounded-lg flex items-center justify-center z-10">
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>{t("admin.userRoles.saving", "Speichere...")}</span>
                    </div>
                  </div>
                )}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                        #{role.id}
                      </span>
                      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{role.name}</h2>
                    </div>
                    {role.description ? (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{role.description}</p>
                    ) : (
                      <p className="text-sm text-slate-400 dark:text-slate-500 mt-1 italic">
                        {t("admin.userRoles.noDescription", "Keine Beschreibung hinterlegt.")}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(role)}
                      className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                      title={t("admin.userRoles.actions.editRole", "Rolle bearbeiten")}
                    >
                      <i className="fas fa-edit text-sm"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteRole(role)}
                      className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                      title={t("admin.userRoles.actions.deleteRole", "Rolle löschen")}
                    >
                      <i className="fas fa-trash text-sm"></i>
                    </button>
                  </div>
                </div>

                <div className="space-y-5">
                  {permissionGroups.map((group) => (
                    <div key={`${role.id}-${group.id}`} className="border border-slate-100 dark:border-slate-700 rounded-lg">
                      <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-600">
                        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 uppercase tracking-wide">
                          {group.title}
                        </h3>
                      </div>
                      <div className="p-4 space-y-2">
                        {group.permissions.map((permission) => {
                          const checked = rolePermissions.has(permission.key);
                          return (
                            <label
                              key={`${role.id}-${permission.key}`}
                              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                                checked ? "border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/30" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:ring-primary/20 dark:focus:ring-primary-light/20 bg-white dark:bg-slate-800"
                                checked={checked}
                                onChange={(event) => handleTogglePermission(role, permission.key, event.target.checked)}
                                disabled={isUpdating}
                              />
                              <span className="flex-1">
                                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">
                                  {permission.label}
                                </span>
                                {permission.description && (
                                  <span className="text-xs text-slate-500 dark:text-slate-400 block mt-1">
                                    {permission.description}
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {roles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-shield-alt text-slate-400 dark:text-slate-500 text-xl"></i>
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
              {t("admin.userRoles.empty.title", "Noch keine Rollen vorhanden")}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {t("admin.userRoles.empty.subtitle", "Lege deine erste Rolle an, um Zugriffe zu strukturieren.")}
            </p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <i className="fas fa-plus text-xs"></i>
              {t("admin.userRoles.actions.addFirstRole", "Erste Rolle anlegen")}
            </button>
          </div>
        )}
      </div>

      <AdminModalTailwind
        isOpen={isRoleModalOpen}
        onClose={closeRoleModal}
        title={
          editingRole
            ? t("admin.userRoles.modal.editTitle", "Rolle bearbeiten")
            : t("admin.userRoles.modal.createTitle", "Neue Rolle anlegen")
        }
        subtitle={
          editingRole
            ? t("admin.userRoles.modal.editSubtitle", "Passe Namen, Beschreibung und Berechtigungen dieser Rolle an.")
            : t("admin.userRoles.modal.createSubtitle", "Definiere eine neue Rolle inklusive Berechtigungen.")
        }
        onSave={handleSubmitRoleForm}
        onCancel={closeRoleModal}
        saveButtonText={
          editingRole
            ? t("admin.userRoles.modal.save", "Änderungen speichern")
            : t("admin.userRoles.modal.create", "Rolle erstellen")
        }
        cancelButtonText={t("admin.cancel", "Abbrechen")}
        isSaving={createRoleMutation.isLoading || updateRoleMutation.isLoading}
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="role-name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t("admin.userRoles.form.name", "Rollenname")}
            </label>
            <input
              id="role-name"
              name="name"
              type="text"
              value={roleForm.name}
              onChange={handleRoleFormChange}
              placeholder={t("admin.userRoles.form.namePlaceholder", "z.B. Scorekeeper")}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
              required
            />
          </div>

          <div>
            <label htmlFor="role-description" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t("admin.userRoles.form.description", "Beschreibung")}
            </label>
            <textarea
              id="role-description"
              name="description"
              value={roleForm.description}
              onChange={handleRoleFormChange}
              placeholder={t("admin.userRoles.form.descriptionPlaceholder", "Kurze Beschreibung der Rolle")}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
              rows={3}
            />
          </div>

          <div className="border border-slate-200 dark:border-slate-700 rounded-lg">
            <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                {t("admin.userRoles.form.permissions", "Berechtigungen")}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t("admin.userRoles.form.permissionsHint", "Wähle aus, welche Bereiche diese Rolle sehen oder verwalten darf.")}
              </p>
            </div>
            <div className="p-4 space-y-4 max-h-72 overflow-y-auto">
              {permissionGroups.map((group) => (
                <div key={`modal-${group.id}`} className="border border-slate-100 dark:border-slate-700 rounded-md">
                  <div className="px-3 py-2 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-600">
                    <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                      {group.title}
                    </h4>
                  </div>
                  <div className="p-3 space-y-2 bg-slate-50/60 dark:bg-slate-700/30">
                    {group.permissions.map((permission) => {
                      const checked = roleForm.permissions.includes(permission.key);
                      return (
                        <label
                          key={`modal-${permission.key}`}
                          className={`flex items-start gap-3 p-2 rounded-md border text-sm transition-colors ${
                            checked ? "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:ring-primary/20 dark:focus:ring-primary-light/20 bg-white dark:bg-slate-800"
                            checked={checked}
                            onChange={() => handleRoleFormPermissionToggle(permission.key)}
                          />
                          <span className="flex-1">
                            <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                              {permission.label}
                            </span>
                            {permission.description && (
                              <span className="text-xs text-slate-500 dark:text-slate-400 block mt-0.5">
                                {permission.description}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AdminModalTailwind>

      <AdminModalTailwind
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={t("admin.userRoles.delete.title", "Rolle löschen")}
        subtitle={
          roleToDelete
            ? t("admin.userRoles.delete.subtitle", 'Soll die Rolle "{{name}}" wirklich gelöscht werden?', {
                name: roleToDelete.name
              })
            : ""
        }
        onSave={() => {
          if (!roleToDelete || deleteRoleMutation.isLoading) return;
          deleteRoleMutation.mutate(roleToDelete.id);
        }}
        onCancel={closeDeleteModal}
        saveButtonText={t("admin.userRoles.delete.confirm", "Löschen")}
        cancelButtonText={t("admin.cancel", "Abbrechen")}
        isSaving={deleteRoleMutation.isLoading}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 rounded-lg">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300">
              {t("admin.userRoles.delete.warning", "Diese Aktion kann nicht rückgängig gemacht werden. Alle Berechtigungszuweisungen gehen verloren.")}
            </p>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t("admin.userRoles.delete.message", "Stelle sicher, dass keine Benutzer mehr diese Rolle benötigen.")}
          </p>
        </div>
      </AdminModalTailwind>

      {toast.show && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${
              toast.type === "success"
                ? "border-green-200 dark:border-green-800"
                : toast.type === "error"
                ? "border-red-200 dark:border-red-800"
                : toast.type === "warning"
                ? "border-yellow-200 dark:border-yellow-800"
                : "border-blue-200 dark:border-blue-800"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  toast.type === "success"
                    ? "bg-green-100 dark:bg-green-900/30"
                    : toast.type === "error"
                    ? "bg-red-100 dark:bg-red-900/30"
                    : toast.type === "warning"
                    ? "bg-yellow-100 dark:bg-yellow-900/30"
                    : "bg-blue-100 dark:bg-blue-900/30"
                }`}
              >
                <i
                  className={`fas text-xs ${
                    toast.type === "success"
                      ? "fa-check text-green-600 dark:text-green-400"
                      : toast.type === "error"
                      ? "fa-times text-red-600 dark:text-red-400"
                      : toast.type === "warning"
                      ? "fa-exclamation-triangle text-yellow-600 dark:text-yellow-400"
                      : "fa-info text-blue-600 dark:text-blue-400"
                  }`}
                />
              </div>
              <span
                className={`text-sm font-medium ${
                  toast.type === "success"
                    ? "text-green-900 dark:text-green-100"
                    : toast.type === "error"
                    ? "text-red-900 dark:text-red-100"
                    : toast.type === "warning"
                    ? "text-yellow-900 dark:text-yellow-100"
                    : "text-blue-900 dark:text-blue-100"
                }`}
              >
                {toast.message}
              </span>
              <button
                onClick={hideToast}
                className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default React.memo(UserRolesTailwind);

